// COMPATIBILIDADE (2026-08-27) — venda × instrumento × ART × atestado, lado a
// lado, para UM contrato.
//
// Pedido do Lucas (25/08): "verificar toda a compatibilidade entre o documento
// de venda, a ART e o atestado". O caso real: ele emitiu ART com km MAIOR que
// o contrato, a pedido do cliente — e não havia onde ver isso.
//
// 🚨 Duas regras de leitura que a tela precisa deixar claras:
//  1. O VEREDITO (vermelho) é só de R$ e datas. Quantitativo aparece lado a
//     lado SEM julgamento: a venda quase sempre é "1 vb" (o agregador de
//     composição fixa quantidade 1), então comparar km com isso daria alarme
//     falso sempre. Quem decide se 45 km está certo é a pessoa.
//  2. A unidade "km" na composição é AMBÍGUA — o editor usa km também para
//     distância de VIAGEM (combustível). A tela avisa em vez de fingir.
//
// Leitura pura: nada aqui grava. Os botões levam para onde se resolve.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Typography, Tag, Spin, Empty, Alert, Flex, Button, Tooltip } from 'antd';
import { API_URL } from '../../config';
import { getToken, fmt, fmtData, num } from './utils';

const { Text } = Typography;

interface Props {
  contratoId: string | null;
  numero?: string;
  onFechar: () => void;
  /** Abre o contrato numa aba específica (mesmo mecanismo da vista). */
  onIrPara: (aba: string) => void;
}

const COR_BORDA = '#f0f0f0';

function Coluna({ titulo, cor, children }: {
  titulo: string; cor: string; children: React.ReactNode;
}) {
  return (
    <div style={{
      flex: '1 1 240px', minWidth: 230, border: `1px solid ${COR_BORDA}`,
      borderRadius: 8, padding: '10px 12px', background: '#fff',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: cor,
        textTransform: 'uppercase', marginBottom: 8,
      }}>{titulo}</div>
      {children}
    </div>
  );
}

function Item({ r, v, dica }: { r: string; v: React.ReactNode; dica?: string }) {
  const corpo = (
    <div style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 12, alignItems: 'baseline' }}>
      <span style={{ color: '#888', minWidth: 74, flexShrink: 0 }}>{r}</span>
      <span style={{ flex: 1, wordBreak: 'break-word' }}>{v ?? <span style={{ color: '#ccc' }}>—</span>}</span>
    </div>
  );
  return dica ? <Tooltip title={dica}>{corpo}</Tooltip> : corpo;
}

/** Lista de quantitativos (ART, atestado, composição) — SEM veredito. */
function Quantitativos({ linhas, vazio }: {
  linhas: { quantidade: any; unidade?: string | null; descricao?: string | null }[];
  vazio: string;
}) {
  if (!linhas?.length) return <Text type="secondary" style={{ fontSize: 11.5 }}>{vazio}</Text>;
  return (
    <div>
      {linhas.map((l, i) => (
        <div key={i} style={{ fontSize: 12, padding: '2px 0' }}>
          <Text strong>{num(l.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</Text>
          {' '}
          <Text type="secondary" style={{ fontSize: 11 }}>{(l.unidade || '').toString().toLowerCase()}</Text>
          {l.descricao && <div style={{ fontSize: 11, color: '#888' }}>{l.descricao}</div>}
        </div>
      ))}
    </div>
  );
}

export function ModalCompatibilidade({ contratoId, numero, onFechar, onIrPara }: Props) {
  const [dados, setDados] = useState<any>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!contratoId) { setDados(null); setErro(null); return; }
    let vivo = true;
    setCarregando(true); setErro(null); setDados(null);
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
          { action: 'op_formalidades_detalhe', contrato_id: contratoId },
          { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        if (vivo) setDados(d);
      } catch (e: any) {
        if (vivo) setErro(e.response?.data?.error || e.message);
      } finally { if (vivo) setCarregando(false); }
    })();
    return () => { vivo = false; };
  }, [contratoId]);

  const c = dados?.contrato;
  const arts: any[] = dados?.arts || [];
  const atestados: any[] = dados?.atestados || [];
  const vereditos: any[] = dados?.vereditos || [];
  const totais = dados?.totais_venda;
  // A ART que a tela mostra é a mesma que o veredito usa: a emitida manda.
  const artRef = arts.find((a) => a.emitida && a.status_crea !== 'dispensada')
    || arts.find((a) => a.status_crea !== 'dispensada') || null;
  const atRef = atestados[0] || null;

  const alertas = vereditos.filter((v) => v.nivel === 'atencao');
  const avisos = vereditos.filter((v) => v.nivel !== 'atencao');

  return (
    <Modal
      open={!!contratoId}
      onCancel={onFechar}
      footer={null}
      title={`Compatibilidade — ${numero || c?.numero || ''}`}
      width="94vw"
      style={{ maxWidth: 1240, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      {carregando && <div style={{ textAlign: 'center', padding: 50 }}><Spin /></div>}
      {erro && <Alert type="error" showIcon message="Não consegui carregar" description={erro} />}

      {c && (
        <>
          <div style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
            {c.titulo}
            {c.cliente_nome && <> · <b>{c.cliente_nome}</b></>}
            {Number(c.aditivos_total) > 0 && (
              <Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>
                {c.aditivos_total} aditivo(s)
              </Tag>
            )}
          </div>

          {/* ── O que precisa de olho ── */}
          {alertas.length > 0 && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message={`${alertas.length} ponto(s) para conferir`}
              description={
                <div>
                  {alertas.map((v, i) => (
                    <div key={i} style={{ marginTop: i ? 6 : 2, fontSize: 12.5 }}>
                      <b>{v.titulo}</b>
                      <div style={{ color: '#666' }}>{v.detalhe}</div>
                    </div>
                  ))}
                </div>
              }
            />
          )}
          {alertas.length === 0 && !carregando && (
            <Alert type="success" showIcon style={{ marginBottom: 12 }}
              message="Valores e datas batem entre contrato, instrumento e ART"
              description="Os quantitativos aparecem abaixo lado a lado — quem confere é você." />
          )}
          {avisos.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              {avisos.map((v, i) => (
                <div key={i} style={{ fontSize: 12, color: '#888', padding: '1px 0' }}>
                  ℹ️ <b style={{ color: '#666' }}>{v.titulo}</b> — {v.detalhe}
                </div>
              ))}
            </div>
          )}

          {/* ── As quatro colunas ── */}
          <Flex gap={10} wrap style={{ alignItems: 'stretch' }}>
            <Coluna titulo="1 · Venda" cor="#0930D0">
              <Item r="Documento" v={c.venda_numero || (c.venda_id ? 'sem número' : null)} />
              <Item r="Tipo" v={c.venda_tipo === 'contrato_externo' ? 'contrato do cliente' : c.venda_tipo} />
              <Item r="Total" v={totais ? fmt(totais.bruto) : null}
                dica="Soma de quantidade × valor unitário dos itens — é o que o kick-off grava no contrato." />
              {totais?.tem_desconto && (
                <Item r="Com desconto" v={fmt(totais.liquido)}
                  dica="O PDF assinado aplica os descontos; o valor do contrato, não. Por isso os dois aparecem." />
              )}
              <Item r="Contrato" v={fmt(num(c.valor_contratado))} />
              {Number(c.aditivos_total) > 0 && (
                <Item r="Com aditivos" v={fmt(num(c.valor_total_com_aditivos))} />
              )}
              <Item r="Prazo" v={`${fmtData(c.data_inicio_prevista)} → ${fmtData(c.data_termino_previsto)}`} />
              <div style={{ marginTop: 8, borderTop: `1px dashed ${COR_BORDA}`, paddingTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>Quantitativo da composição</Text>
                <Quantitativos
                  linhas={dados.composicao_itens || []}
                  vazio="A venda não veio de composição — o quantitativo está no PDF." />
                {(dados.composicao_itens || []).some((i: any) =>
                  String(i.unidade || '').toLowerCase() === 'km') && (
                  <Text type="secondary" style={{ fontSize: 10.5, display: 'block', marginTop: 4 }}>
                    ⚠️ "km" na composição também é usado para distância de viagem —
                    confira se é a extensão do serviço.
                  </Text>
                )}
              </div>
              <Button size="small" type="link" style={{ padding: 0, marginTop: 6 }}
                onClick={() => onIrPara('visao')}>abrir o contrato →</Button>
            </Coluna>

            <Coluna titulo="2 · Instrumento" cor="#7C3AED">
              {c.juridico_id ? (
                <>
                  {c.juridico_sensivel ? (
                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                      Contrato de vínculo — o conteúdo não aparece aqui.
                    </Text>
                  ) : (
                    <>
                      <Item r="Documento" v={c.juridico_codigo || 'PDF no acervo'} />
                      <Item r="Contraparte" v={c.juridico_contraparte} />
                      <Item r="Valor" v={c.juridico_valor != null ? fmt(num(c.juridico_valor)) : null} />
                      <Item r="Vigência" v={c.juridico_vigencia_fim
                        ? `${fmtData(c.juridico_vigencia_inicio)} → ${fmtData(c.juridico_vigencia_fim)}`
                        : 'sem prazo cadastrado'} />
                      <Item r="Situação" v={c.juridico_situacao} />
                      {Number(c.juridico_cobre_qtd) > 1 && (
                        <Item r="Cobre" v={`${c.juridico_cobre_qtd} projetos`}
                          dica="Contrato guarda-chuva: o mesmo papel formaliza vários projetos." />
                      )}
                    </>
                  )}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 11.5 }}>
                  O contrato assinado ainda não está no acervo.
                </Text>
              )}
            </Coluna>

            <Coluna titulo="3 · ART" cor="#B07500">
              {artRef ? (
                <>
                  <Item r="Número" v={artRef.numero || 'sem número CREA'} />
                  <Item r="Situação" v={
                    <>
                      {artRef.emitida
                        ? <Tag color="green">emitida</Tag>
                        : <Tag color="orange">sem PDF</Tag>}
                      {artRef.status_crea && (
                        <Text type="secondary" style={{ fontSize: 11 }}>{artRef.status_crea}</Text>
                      )}
                    </>
                  } />
                  <Item r="RT" v={artRef.rt_nome || artRef.rt_email} />
                  <Item r="Valor" v={artRef.valor != null ? fmt(num(artRef.valor)) : null} />
                  <Item r="Período" v={`${fmtData(artRef.inicio_art)} → ${fmtData(artRef.fim_art)}`} />
                  <div style={{ marginTop: 8, borderTop: `1px dashed ${COR_BORDA}`, paddingTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Quantitativo declarado</Text>
                    <Quantitativos linhas={artRef.linhas || []}
                      vazio="Sem linhas de atividade — o rascunho do kick-off nasce sem elas." />
                  </div>
                  {arts.length > 1 && (
                    <Text type="secondary" style={{ fontSize: 10.5, display: 'block', marginTop: 4 }}>
                      +{arts.length - 1} outra(s) ART neste contrato.
                    </Text>
                  )}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 11.5 }}>Nenhuma ART cadastrada.</Text>
              )}
              <Button size="small" type="link" style={{ padding: 0, marginTop: 6 }}
                onClick={() => onIrPara('arts')}>abrir ARTs →</Button>
            </Coluna>

            <Coluna titulo="4 · Atestado" cor="#0a7f4a">
              {atRef ? (
                <>
                  <Item r="Número" v={atRef.numero} />
                  <Item r="Situação" v={atRef.status} />
                  <Item r="CAT" v={atRef.cat_numero} />
                  <div style={{ marginTop: 8, borderTop: `1px dashed ${COR_BORDA}`, paddingTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Quantitativo atestado</Text>
                    <Quantitativos linhas={atRef.quantitativo || []}
                      vazio="Sem quantitativo — ele é derivado das linhas da ART." />
                  </div>
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 11.5 }}>
                  Nenhum atestado emitido para este contrato.
                </Text>
              )}
              <Button size="small" type="link" style={{ padding: 0, marginTop: 6 }}
                onClick={() => onIrPara('atestados')}>abrir Atestados →</Button>
            </Coluna>
          </Flex>
        </>
      )}

      {!carregando && !erro && !c && <Empty description="Sem dados" />}
    </Modal>
  );
}
